import { Injectable } from '@angular/core';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import PictureFillSymbol from '@arcgis/core/symbols/PictureFillSymbol';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import HeatmapRenderer from '@arcgis/core/renderers/HeatmapRenderer';
import Color from '@arcgis/core/Color';
import type { FeatureReductionClusterProperties } from '@arcgis/core/layers/support/FeatureReductionCluster';
import type { AggregateFieldProperties } from '@arcgis/core/layers/support/AggregateField';
import type { LabelClassProperties } from '@arcgis/core/layers/support/LabelClass';

export interface LayerStyleConfig {
  fill?: string | null;
  fillOpacity?: number;
  stroke?: string | null;
  strokeWidth?: number;
  strokeOpacity?: number;
  markerSize?: number;
  markerStyle?: string;
  markerOutline?: string;
  markerSymbol?: string;
  pictureFillUrl?: string;
  pictureFillWidth?: number;
  pictureFillHeight?: number;
  uniqueValueField?: string;
  uniqueValues?: { value: string; label?: string; color?: string }[];
}

/** Configuración opcional del renderer de flujo animado (CIMSymbolAnimationMoveAlongLine). */
export interface AnimatedFlowConfig {
  /** Color RGBA de la línea base (0-255). */
  lineColor?: number[];
  lineWidth?: number;
  /** Grosor del halo difuso bajo la línea (0 = sin halo). */
  haloWidth?: number;
  /** Color RGBA del marcador (gota/flecha). */
  markerColor?: number[];
  /** 'droplet' = gota/cometa redondeada (por defecto) · 'arrow' = flecha clásica. */
  markerShape?: 'droplet' | 'arrow';
  /** Tamaño del marcador en puntos. */
  markerSize?: number;
  /** Separación entre marcadores a lo largo de la línea, en puntos. */
  placementTemplate?: number;
  /**
   * Velocidad del movimiento en puntos/segundo (unidades de pantalla): se ve
   * constante a cualquier zoom. Valores altos (>45) "parpadean" en tramos
   * cortos al hacer mucho zoom; 25-35 da una sensación de caudal suave.
   */
  speed?: number;
  /** true = loop continuo sin saltos (efecto de flujo constante). */
  continuous?: boolean;
  /** true = desfase de inicio aleatorio por feature (más orgánico, evita el efecto "caravana"). */
  randomizeStart?: boolean;
  /** true = invierte el sentido del movimiento (útil si la digitalización del tramo va contra el flujo real). */
  reverse?: boolean;
}

const ANIMATED_FLOW_DEFAULTS: Required<AnimatedFlowConfig> = {
  lineColor: [55, 138, 221, 255], // azul "Ríos" (#378ADD) de la paleta del  
  lineWidth: 1.5,
  haloWidth: 5,
  markerColor: [235, 250, 255, 235], // blanco-cian suave
  markerShape: 'droplet',
  markerSize: 6,
  placementTemplate: 34,
  speed: 30,
  continuous: true,
  randomizeStart: true,
  reverse: false,
};

/** Geometría de la flecha clásica (patrón de la muestra oficial "Arrows along a line"). */
const ARROW_GEOMETRY = { rings: [[[-8, -5.47], [-8, 5.6], [1.96, -0.03], [-8, -5.47]]] } as const;

/**
 * Genera el anillo suave de una "gota de flujo" (cardioide): cabeza redonda
 * hacia +X y cola afinada hacia -X; se orienta con `angleToLine` al sentido
 * del trazo. El muestreo denso evita el aspecto duro de un triángulo.
 */
function buildFlowDropletRing(size = 3, cuspX = -3, steps = 72): number[][] {
  const ring: number[][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    const r = size * (1 + Math.cos(t)); // r=0 en la cúspide (θ=π) · r=2·size en la cabeza (θ=0)
    ring.push([r * Math.cos(t) + cuspX, r * Math.sin(t)]);
  }
  return ring;
}

/** Configuración opcional del símbolo de relleno con pulso de alerta (SENAMHI N3/N4). */
export interface PulseFillConfig {
  /** Color RGBA del relleno (su alfa define desde dónde inicia el latido). */
  fillColor?: number[];
  /** Color RGBA del contorno (estático). */
  strokeColor?: number[];
  strokeWidth?: number;
  /** Duración de un ciclo completo del latido, en segundos. */
  duration?: number;
  /** false = sin contorno: el relleno queda como único trazo de la geometría. */
  bordered?: boolean;
  /** false = relleno estático, sin latido (el aviso se lee por su color). */
  pulsing?: boolean;
}

const PULSE_FILL_DEFAULTS: Required<PulseFillConfig> = {
  fillColor: [244, 67, 54, 140], // rojo SENAMHI N4 (translúcido)
  strokeColor: [183, 28, 28, 255], // #B71C1C
  strokeWidth: 1.4,
  duration: 1.4,
  bordered: true,
  pulsing: true,
};

/**
 * Configuración del relleno de alerta sensible a la escala: el mismo símbolo con latido más
 * una variante estática que el SDK usa según la escala de la vista (`alternateSymbols` de
 * `UniqueValueInfo` / `ClassBreakInfo`, novedad de la SDK 5.1).
 */
export interface AlertScaleSymbolConfig extends PulseFillConfig {
  /**
   * Denominador de escala 1:N donde cambia el símbolo: en la vista más alejada que esa escala
   * se dibuja el pulso animado y al acercarse, la variante estática. 0/undefined = pulso a
   * cualquier escala (sin variantes).
   */
  switchScale?: number;
}

/** Par que espera `addUniqueValueInfo` / `addClassBreakInfo`: símbolo + variantes por escala. */
export interface AlertScaleSymbols {
  symbol: CIMSymbol;
  alternateSymbols?: CIMSymbol[];
}

/**
 * Símbolo dedicado de la burbuja de agrupamiento (`featureReduction.symbol`, SDK >= 4.25).
 *
 * Es la pieza que da identidad propia al agrupamiento **sin tocar la simbología de los puntos
 * individuales**: a diferencia de `featureReduction.renderer` —que repinta también las features
 * sueltas y anula `clusterMinSize`/`clusterMaxSize` si lleva una `SizeVariable`—, `symbol` solo
 * sustituye el estilo de las burbujas, así que el punto individual conserva el del servicio.
 */
export interface ClusterSymbolConfig {
  /**
   * Color de relleno de la burbuja. Si se omite, se deriva del renderer de la capa en runtime
   * (`resolveRendererColor`), de modo que la burbuja comparte identidad cromática con la capa.
   */
  color?: string;
  /** Color del contorno que separa burbujas contiguas y las despega del mapa base. */
  outlineColor?: string;
  /** Grosor del contorno de la burbuja, en px. */
  outlineWidth?: number;
}

const CLUSTER_SYMBOL_DEFAULTS: Required<Omit<ClusterSymbolConfig, 'color'>> = {
  // Contorno claro: la etiqueta del cluster es texto blanco en negrita, así que un halo claro
  // alrededor del círculo refuerza el contraste y separa burbujas que se tocan.
  outlineColor: '#FFFFFF',
  outlineWidth: 1.5,
};

/** Configuración del agrupamiento de puntos (`featureReduction` de tipo `cluster`). */
export interface ClusterPointsConfig {
  /** Radio del área de influencia de cada cluster, en px (a mayor radio, menos grupos). */
  radius?: number;
  /** Tamaño mínimo del símbolo del cluster, en px: debe dejar sitio al conteo y superar el tamaño de un punto individual. */
  minSize?: number;
  /** Tamaño máximo del símbolo del cluster, en px. */
  maxSize?: number;
  /**
   * Escala máxima a la que se mantiene el agrupamiento: al acercarse más (denominador
   * de escala menor) el cluster se resuelve en puntos individuales. `undefined` =
   * agrupar a cualquier escala (comportamiento por defecto del SDK).
   */
  maxScale?: number;
  /** Campos que la etiqueta y el popup resumen de cada cluster (suma de población, estado predominante…). */
  aggregates?: ClusterAggregateConfig[];
  /** Tamaño de la fuente de la etiqueta del cluster, en puntos (`Text` con halo debajo). */
  labelSize?: number;
  /**
   * Escalera de la etiqueta: un tramo por rango de conteo, para que **la letra Accompañe al
   * tamaño de la burbuja**. El SDK reparte el diámetro de la burbuja entre `clusterMinSize`
   * (conteo 1) y `clusterMaxSize` (conteo máximo), pero `Font.size` es un valor estático —no
   * admite expresión—, así que la única forma de acoplar una a otra es una clase de etiqueta
   * por rango (`LabelClass.where`). Si se define, sustituye a `labelSize`.
   *
   * Los tramos deben ser contiguos y sin solaparse; el último se toma como "resto".
   */
  labelSteps?: ClusterLabelStep[];
  /**
   * Estrategia de descarte de etiquetas solapadas. Por defecto `'none'`, como recomienda el
   * SDK para etiquetas centradas en la burbuja: con `'static'` el texto de un cluster se
   * descarta al chocar con la burbuja o el texto vecino, y a escala nacional eso deja la
   * vista sin conteos. Subirla a `'static'` solo si se prefiere menos texto antes que todos.
   */
  deconflictionStrategy?: 'none' | 'static';
}

/**
 * Un tramo de la escalera de etiqueta del cluster: los clusters cuyo `cluster_count` cae en
 * `(maxCountAnterior, maxCount]` se etiquetan con `size` píxeles y, según `includeSummaries`, con
 * los renglones de resumen además del conteo.
 */
export interface ClusterLabelStep {
  /** Conteo máximo **inclusive** que cubre este tramo. El último tramo se toma como "resto". */
  maxCount: number;
  /** Tamaño de la fuente para este tramo, en píxeles. */
  size: number;
  /** `true` = añade un renglón por resumen; `false` = muestra solo el conteo ("9"). */
  includeSummaries: boolean;
  /**
   * Tope de renglones de la etiqueta. Los resúmenes que no quepan no se pierden: siguen en el
   * popup del cluster y en la leyenda. Existe porque el texto tiene que caber **dentro** de la
   * burbuja: tres renglones a 15 px (~54 px) se salen de una burbuja de 40 px y acaban
   * invadiendo a las vecinas, que es justo el efecto que se quería evitar.
   */
  maxLines?: number;
}

/**
 * Resumen de un campo del servicio para cada cluster (`AggregateField`, SDK >= 4.25):
 * suma de población, habitantes atendidos, estado predominante de la zona, etc.
 */
export interface ClusterAggregateConfig {
  /** Campo del servicio que se resume; se omite solo para `statisticType: 'count'`. */
  field?: string;
  /** Operación de resumen: `sum`, `avg`, `min`, `max`, `mode` (valor predominante) o `count`. */
  statisticType?: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'mode';
  /** Nombre legible del resumen en el popup y la leyenda; por defecto se deriva del campo. */
  alias?: string;
  /** Texto que precede al valor en la etiqueta del cluster (p. ej. "POB"). */
  labelPrefix?: string;
  /** Unidad que sigue al valor en la etiqueta del cluster (p. ej. "hab."). */
  unit?: string;
  /** false = el resumen se muestra solo en el popup, no en la etiqueta del cluster. */
  inLabel?: boolean;
  /** false = el campo es de texto (p. ej. el estado predominante) y no se formatea como número. */
  numeric?: boolean;
}

/** Resumen ya normalizado (nombre técnico + textos de la etiqueta) listo para el SDK. */
interface ClusterAggregateResolved {
  name: string;
  alias: string;
  statisticType: NonNullable<ClusterAggregateConfig['statisticType']>;
  onStatisticField?: string;
  labelPrefix: string;
  unit: string;
  inLabel: boolean;
  numeric: boolean;
}


/**
 * Tamaño mínimo del símbolo del cluster cuando la etiqueta lleva más de una línea
 * (conteo + resúmenes): con los 24 px por defecto el texto se sale del círculo.
 */
const CLUSTER_MIN_SIZE_WITH_AGGREGATES = 32;

/**
 * Escalera por defecto de la etiqueta del cluster.
 *
 * El reparto real es: `cluster_count = 1` → `clusterMinSize` px, `cluster_count` máximo →
 * `clusterMaxSize` px, interpolado linealmente por el `SizeVariable` que el SDK añade al
 * renderer del cluster. Como el máximo del conteo cambia con los datos y con el zoom —una capa
 * de 536 puntos a escala nacional y la misma a escala de detalle dan repartos muy
 * distintos—, los cortes se fijan en **conteos absolutos** y no como fracción del máximo. Es
 * lo que hace que la escalera degrade con gracia: si el máximo de los datos nunca alcanza el
 * último tramo, las burbujas grandes se etiquetan con el tramo intermedio (legible) en vez de
 * dejar de etiquetarse.
 *
 * Los tres tramos cubren lo que se pidió: letra discreta en las burbujas pequeñas (que es
 * donde se amontona el texto y donde se solapaba), letra mayor en las grandes, y los resúmenes
 * solo cuando la burbuja tiene sitio de sobra —con dos renglones como tope— porque el conteo
 * solo es el "de qué va" y el resto está en el popup y en la leyenda.
 */
const CLUSTER_LABEL_STEPS: ClusterLabelStep[] = [
  { maxCount: 9, size: 10, includeSummaries: false },
  { maxCount: 49, size: 12, includeSummaries: false },
  { maxCount: Number.POSITIVE_INFINITY, size: 15, includeSummaries: true, maxLines: 2 },
];

/**
 * Defaults de agrupamiento más legibles que los del SDK. En `@arcgis/core` 5.1.x el runtime
 * usa `clusterRadius` 80px, `clusterMinSize` 12px y `clusterMaxSize` 50px (aunque la
 * documentación de la API sigue citando 60/9/37.5): 12px no deja sitio para el conteo.
 *
 * El par mínimo/máximo es también la forma en que el cluster comunica su **densidad**: sin
 * `SizeVariable` en el renderer (que redimensionaría además los puntos individuales) el SDK
 * reparte el conteo de cada burbuja dentro de ese rango, así que un rango amplio separa mejor
 * una burbuja de 2 puntos de otra de 300.
 */
const CLUSTER_POINTS_DEFAULTS: Required<
  Pick<ClusterPointsConfig, 'radius' | 'minSize' | 'maxSize' | 'labelSize' | 'labelSteps'>
> = {
  radius: 60,
  minSize: 24,
  maxSize: 64,
  labelSize: 12,
  labelSteps: CLUSTER_LABEL_STEPS,
};

/** Configuración del mapa de calor de puntos (`HeatmapRenderer`, solo mapas 2D). */
export interface HeatmapPointsConfig {
  /** Campo numérico que pondera cada punto (p. ej. "VIV2017" = viviendas); sin él, cada punto pesa 1. */
  field?: string;
  /** Radio de influencia de cada punto, en px: a mayor radio, manchas más suaves y amplias. */
  radius?: number;
  /** Escala de referencia (denominador 1:N) a la que se calibran el radio y la densidad; 0 = sin referencia. */
  referenceScale?: number;
  /** Densidad que se mapea a la parada de color final; 0 = la calcula el SDK con los datos visibles. */
  maxDensity?: number;
  /** Densidad mínima dibujada (valores altos atenúan el "ruido" de densidad baja). */
  minDensity?: number;
  /** Paradas de la rampa de densidad, de menor (`ratio` 0) a mayor (`ratio` 1). */
  colorStops?: { ratio: number; color: string }[];
  /** Expresión Arcade numérica como alternativa a `field` (solo mapas 2D). */
  valueExpression?: string;
}

/**
 * Defaults del mapa de calor: radio del SDK (18 px) y rampa cálida sobre fondo claro,
 * transparente en el extremo bajo para no "lavar" el mapa base en zonas de densidad casi nula.
 * `maxDensity`/`minDensity` en 0: el SDK calcula la densidad máxima según los datos visibles,
 * así la rampa se adapta a cualquier escala sin recalibrar a mano.
 */
const HEATMAP_POINTS_DEFAULTS: Required<
  Pick<HeatmapPointsConfig, 'radius' | 'referenceScale' | 'maxDensity' | 'minDensity' | 'colorStops'>
> = {
  radius: 18,
  referenceScale: 0,
  maxDensity: 0,
  minDensity: 0,
  colorStops: [
    { ratio: 0, color: 'rgba(255, 255, 255, 0)' },
    { ratio: 0.25, color: '#FFE9B3' },
    { ratio: 0.5, color: '#F7C948' },
    { ratio: 0.75, color: '#EE7B2B' },
    { ratio: 1, color: '#C0392B' },
  ],
};

/** Configuración de una etiqueta de feature (`LabelClass`) aplicada en el cliente. */
export interface LabelFieldConfig {
  /** Campo del servicio usado como texto de la etiqueta (p. ej. "QUEBRADA"). */
  field?: string;
  /** Tamaño de la fuente en puntos. */
  size?: number;
  /** Color del texto. */
  color?: string;
  /** Color del halo que separa el texto del mapa base. */
  haloColor?: string;
  haloSize?: number;
  /** Ubicación respecto al símbolo: los puntos admiten `*-center`; líneas y polígonos solo `*-along`/`center-*`. */
  placement?: 'above-center' | 'below-center' | 'center-center' | 'above-along' | 'below-along' | 'center-along';
  /** Escala más alejada (denominador 1:N) a la que se ven las etiquetas; 0/undefined = sin límite. */
  minScale?: number;
  /** Escala más cercana (denominador 1:N) a la que se ven las etiquetas; 0/undefined = sin límite. */
  maxScale?: number;
  /** 'none' = mostrar siempre (aunque se solapen); 'static' = descartar las etiquetas solapadas. */
  deconflictionStrategy?: 'none' | 'static';
}

const LABEL_FIELD_DEFAULTS: Required<
  Pick<LabelFieldConfig, 'size' | 'color' | 'haloColor' | 'haloSize' | 'placement' | 'deconflictionStrategy'>
> = {
  size: 11,
  color: '#1F2A37',
  haloColor: 'rgba(255, 255, 255, 0.9)',
  haloSize: 1.2,
  placement: 'above-center',
  // Por defecto el SDK descarta etiquetas solapadas; en capas de pocos puntos
  // (barreras, maquinaria) interesa ver todas y decidir el zoom a mano.
  deconflictionStrategy: 'none',
};

@Injectable({ providedIn: 'root' })
export class LayerSymbolFactory {
  createRenderer(config: LayerStyleConfig): SimpleRenderer | UniqueValueRenderer {
    if (config.uniqueValueField && config.uniqueValues && config.uniqueValues.length > 0) {
      return this.createUniqueValueRenderer(config);
    }
    return this.createSimpleRenderer(config);
  }

  private createSimpleRenderer(config: LayerStyleConfig): SimpleRenderer {
    const symbol = this.createSymbol(config);
    return new SimpleRenderer({ symbol });
  }

  private createUniqueValueRenderer(config: LayerStyleConfig): UniqueValueRenderer {
    const renderer = new UniqueValueRenderer({
      field: config.uniqueValueField!,
      defaultSymbol: this.createSymbol(config),
    });
    for (const uv of config.uniqueValues!) {
      const sym = this.createSymbol({ ...config, fill: uv.color ?? config.fill });
      renderer.addUniqueValueInfo({ value: uv.value, symbol: sym, label: uv.label ?? uv.value });
    }
    return renderer;
  }

  createSymbol(config: LayerStyleConfig): SimpleMarkerSymbol | SimpleLineSymbol | SimpleFillSymbol | PictureFillSymbol {
    if (config.pictureFillUrl) {
      return new PictureFillSymbol({
        url: config.pictureFillUrl,
        width: config.pictureFillWidth ?? 24,
        height: config.pictureFillHeight ?? 24,
        outline: config.stroke
          ? new SimpleLineSymbol({ color: config.stroke, width: config.strokeWidth ?? 1 })
          : undefined,
      });
    }
    if (config.markerSymbol) {
      return new SimpleMarkerSymbol({
        style: config.markerStyle as any ?? 'circle',
        color: config.fill ? new Color(config.fill) : new Color([0, 0, 0, 0]),
        size: config.markerSize ?? 12,
        outline: config.markerOutline
          ? new SimpleLineSymbol({ color: config.markerOutline, width: config.strokeWidth ?? 1 })
          : undefined,
      });
    }
    return new SimpleFillSymbol({
      color: config.fill ? new Color([...(this.hexToRgb(config.fill!)), config.fillOpacity ?? 0.5]) : new Color([0, 0, 0, 0]),
      outline: config.stroke
        ? new SimpleLineSymbol({ color: new Color([...(this.hexToRgb(config.stroke!)), config.strokeOpacity ?? 1]), width: config.strokeWidth ?? 1 })
        : undefined,
    });
  }

  createBoundaryRenderer(): SimpleRenderer {
    const symbol = new SimpleLineSymbol({
      color: new Color([255, 255, 255, 0.8]),
      width: 2,
      style: 'solid',
    });
    return new SimpleRenderer({ symbol });
  }

  /**
   * Renderer de flujo animado para capas de línea: CIMSymbol (CIMLineSymbol)
   * con "gotas de flujo" desplazándose a lo largo de la geometría mediante la
   * animación `CIMSymbolAnimationMoveAlongLine` (beta, SDK >= 5.1).
   *
   * Por defecto dibuja un halo suave bajo el cauce y gotas redondeadas (con
   * cola) que fluyen de forma continua y desincronizada entre tramos. Con
   * `markerShape: 'arrow'` se recupera la flecha clásica de la muestra oficial.
   *
   * Caso de uso: sentido de flujo en ríos / tramos hidrológicos (ANA / SNIRH).
   * Limitaciones conocidas del SDK 5.1: solo MapView (2D), no se refleja en la
   * leyenda y el tipo de animación aún no forma parte de la unión
   * `CIMSymbolAnimation`, por lo que el JSON se construye tipado como `any`.
   */
  createAnimatedFlowRenderer(config?: AnimatedFlowConfig): SimpleRenderer {
    const c = { ...ANIMATED_FLOW_DEFAULTS, ...config };
    const symbolData: any = {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMLineSymbol',
        symbolLayers: [
          {
            // Halo difuso que da profundidad al cauce
            type: 'CIMSolidStroke',
            enable: true,
            width: c.haloWidth,
            color: [c.lineColor[0], c.lineColor[1], c.lineColor[2], 60],
          },
          {
            type: 'CIMSolidStroke',
            enable: true,
            width: c.lineWidth,
            color: c.lineColor,
          },
          {
            type: 'CIMVectorMarker',
            enable: true,
            size: c.markerSize,
            markerPlacement: {
              type: 'CIMMarkerPlacementAlongLineSameSize',
              endings: 'WithMarkers',
              placementTemplate: [c.placementTemplate],
              angleToLine: true,
            },
            frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
            markerGraphics: [
              {
                type: 'CIMMarkerGraphic',
                geometry: c.markerShape === 'arrow' ? ARROW_GEOMETRY : { rings: [buildFlowDropletRing()] },
                symbol: {
                  type: 'CIMPolygonSymbol',
                  symbolLayers: [
                    { type: 'CIMSolidFill', enable: true, color: c.markerColor },
                    // Contorno sutil solo en la gota, para definirla sobre el halo
                    ...(c.markerShape === 'droplet'
                      ? [{ type: 'CIMSolidStroke', enable: true, width: 0.5, color: [30, 90, 160, 140] }]
                      : []),
                  ],
                },
              },
            ],
            animations: [
              {
                type: 'CIMSymbolAnimationMoveAlongLine',
                movementType: 'Speed',
                speed: c.speed,
                continuous: c.continuous,
                animatedSymbolProperties: {
                  type: 'CIMAnimatedSymbolProperties',
                  playAnimation: true,
                  repeatType: 'Loop',
                  easing: 'Linear',
                  randomizeStartTime: c.randomizeStart,
                  reverseAnimation: c.reverse,
                },
              },
            ],
          },
        ],
      },
    };
    return new SimpleRenderer({ symbol: new CIMSymbol({ data: symbolData }) });
  }

  /**
   * Símbolo CIM de relleno con **pulso de alerta**: CIMSymbol (CIMPolygonSymbol)
   * cuyo relleno oscila hacia transparente mediante `CIMSymbolAnimationTransparency`
   * (SDK >= 4.31) para atraer la atención sobre features críticas (p. ej.
   * avisos SENAMHI nivel 3/4) sin mover la geometría. El contorno queda
   * estático y sólido como ancla visual.
   *
   * Limitaciones del SDK: no soportado en SceneView ni en la leyenda.
   */
  createPulseFillSymbol(config?: PulseFillConfig): CIMSymbol {
    const c = { ...PULSE_FILL_DEFAULTS, ...config };
    return new CIMSymbol({ data: { type: 'CIMSymbolReference', symbol: this.buildAlertFillShape(c, true) } });
  }

  /**
   * Relleno de alerta con **variante por escala** (`alternateSymbols`, novedad SDK 5.1): mismo
   * aspecto, dos comportamientos. En la vista alejada el aviso es pequeño en pantalla, así que
   * se dibuja el pulso animado para llamar la atención; al acercarse más de `switchScale` el
   * polígono ya es grande y el SDK cambia a un relleno estático que se lee mejor y ahorra el
   * coste de animar muchas features a la vez.
   *
   * Solo 2D y solo para `CIMSymbol`: los símbolos sin rango de escala no admiten variantes.
   */
  createAlertScaleSymbols(config?: AlertScaleSymbolConfig): AlertScaleSymbols {
    const c = { ...PULSE_FILL_DEFAULTS, ...config };
    // Sin latido: un único símbolo estático para cualquier escala.
    if (c.pulsing === false) {
      return {
        symbol: new CIMSymbol({
          data: { type: 'CIMSymbolReference', symbol: this.buildAlertFillShape(c, false) },
        }),
      };
    }
    const switchScale = c.switchScale ?? 0;
    if (!switchScale) {
      return { symbol: this.createPulseFillSymbol(c) };
    }
    return {
      // El motor (`getScaleDependentSymbol`) elige el PRIMER símbolo cuyo rango de escala cubre
      // la vista: `maxScale` es el límite "más acercado" (`view.scale > maxScale`) y `minScale`
      // el "más alejado" (`view.scale <= minScale`), con 0 = sin límite en ese extremo.
      symbol: this.buildAlertFillSymbol(0, switchScale, c, true),
      alternateSymbols: [this.buildAlertFillSymbol(switchScale, 0, c, false)],
    };
  }

  /** Referencia CIM con rango de escala (`data.minScale` / `data.maxScale`) y relleno opcionalmente animado. */
  private buildAlertFillSymbol(
    minScale: number,
    maxScale: number,
    config: Required<PulseFillConfig>,
    animated: boolean,
  ): CIMSymbol {
    return new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        minScale,
        maxScale,
        symbol: this.buildAlertFillShape(config, animated),
      },
    });
  }

  /** Cuerpo CIM (`CIMPolygonSymbol`) del relleno de alerta: contorno estático + relleno con o sin latido. */
  private buildAlertFillShape(config: Required<PulseFillConfig>, animated: boolean): any {
    return {
      type: 'CIMPolygonSymbol',
      symbolLayers: [
        // Contorno estático: ancla visual del aviso. Se omite con `bordered: false`
        // (p. ej. cuando otra capa ya dibuja los límites territoriales).
        ...(config.bordered
          ? [
              {
                type: 'CIMSolidStroke',
                enable: true,
                width: config.strokeWidth,
                color: config.strokeColor,
              },
            ]
          : []),
        {
          type: 'CIMSolidFill',
          enable: true,
          color: config.fillColor,
          // La transparencia base del fill es el punto de partida;
          // Oscillate la lleva hasta 100% (invisible) y regresa.
          ...(animated
            ? {
              animations: [
                {
                  type: 'CIMSymbolAnimationTransparency',
                  toTransparency: 100,
                  animatedSymbolProperties: {
                    type: 'CIMAnimatedSymbolProperties',
                    playAnimation: true,
                    repeatType: 'Oscillate',
                    easing: 'EaseInOut',
                    duration: config.duration,
                  },
                },
              ],
            }
            : {}),
        },
      ],
    };
  }

  /**
   * Agrupamiento nativo de puntos (`featureReduction.type = 'cluster'`, SDK >= 4.16).
   *
   * Mejora sobre la configuración mínima del SDK en tres frentes, sin tocar la arquitectura:
   *
   * 1. **Símbolo propio de la burbuja** (`symbol`). Sin él el SDK infiere el estilo del cluster
   *    desde el renderer de la capa, así que la burbuja se ve igual que los puntos individuales
   *    y solo cambia el tamaño: no hay jerarquía visual entre "un punto" y "puntos agrupados",
   *    que es justo lo que hace el mapa poco legible. `symbol` (y no `renderer`) es la pieza
   *    correcta porque sustituye únicamente el estilo de las burbujas y **deja intacta la
   *    simbología de las features sueltas**; además sigue respetándose `clusterMinSize` /
   *    `clusterMaxSize` para comunicar densidad.
   * 2. **Etiqueta centrada cuya letra se ajusta a la burbuja**, con el conteo
   *    (`$feature.cluster_count`) y, si hay `aggregates` y sitio de sobra, un renglón por
   *    resumen: "536" / "536 / POB 1.234". La letra no es un tamaño fijo, sino una **escalera**
   *    de tres tramos (10/12/15 px) por rango de conteo —una `LabelClass` con su `where` y su
   *    `Font.size` por tramo—, porque `Font.size` es estático y el diámetro de la burbuja no lo
   *    es: con una sola letra fija se sale de las burbujas pequeñas y se monta con las vecinas.
   *    Los resúmenes los calcula el cliente con los puntos que caen dentro de cada burbuja, así
   *    que no cuestan consultas extra al servicio.
   * 3. **Deconflicción acorde al ancho de la etiqueta**: `'none'` solo cuando la etiqueta es el
   *    conteo único y también con varias líneas: las etiquetas van centradas dentro de la
   *    burbuja, así que el SDK desaconseja la deconflicción en cualquier caso (ver
   *    `deconflictionStrategy` más abajo).
   *
   * `maxScale` (denominador 1:N) devuelve la capa a puntos individuales al acercarse: el
   * agrupamiento se mantiene en la vista nacional y desaparece en la vista de detalle.
   *
   * El popup del cluster va desactivado (`popupEnabled: false`) porque el clic sobre la burbuja
   * está reservado para **acercar la vista** al recuadro de los puntos agrupados
   * (`MapNotifier.zoomToCluster`, que consulta el recuadro con `Query.aggregateIds`); el resumen
   * de la zona se ve en la etiqueta y, al acercarse, en los puntos individuales. El modo
   * identificación también ignora los agregados para no abrir un popup encima del vuelo (ver
   * `PopupInteractionNotifier`).
   */
  createClusterReduction(config?: ClusterPointsConfig): FeatureReductionClusterProperties & { type: 'cluster' } {
    const c = { ...CLUSTER_POINTS_DEFAULTS, ...config };
    const aggregates = (c.aggregates ?? []).map(aggregate => this.resolveClusterAggregate(aggregate));
    return {
      type: 'cluster',
      clusterRadius: c.radius,
      // El mínimo del símbolo se agranda solo si **el tramo más pequeño lleva texto de varias
      // líneas**, porque es el que decide: si el texto cabe en la burbuja mínima, cabe en todas
      // las demás. Con la escalera por defecto los tramos pequeños muestran solo el conteo, así
      // que el mínimo se queda en `c.minSize` y la vista deja de amontonarse.
      clusterMinSize: this.needsBiggerMinSize(aggregates, c.labelSteps)
        ? Math.max(c.minSize, CLUSTER_MIN_SIZE_WITH_AGGREGATES)
        : c.minSize,
      clusterMaxSize: c.maxSize,
      labelsVisible: true,
      // El símbolo de la burbuja no se fija aquí: depende del renderer real de la capa, que
      // llega con la capa cargada, así que lo aplica `ArcGisLayerFactory.applyClusterSymbol`
      // (muta `featureReduction.symbol` sin alterar el resto de la configuración).
      //
      // Resúmenes de los puntos que caen en cada burbuja (`AggregateField`, SDK >= 4.25):
      // alimentan la etiqueta, la leyenda y el popup del cluster sin consultas extra al
      // servicio. Sin resúmenes `fields` se omite y el SDK solo expone sus campos
      // automáticos (`cluster_count`, `cluster_avg_*`), que son los que ya usa la etiqueta.
      ...(aggregates.length
        ? {
          fields: aggregates.map(aggregate => ({
            name: aggregate.name,
            alias: aggregate.alias,
            statisticType: aggregate.statisticType,
            // `count` no necesita campo de origen.
            ...(aggregate.onStatisticField ? { onStatisticField: aggregate.onStatisticField } : {}),
          })),
        }
        : {}),
      // Etiquetas exclusivas de clusters: son independientes de layer.labelingInfo.
      // Una clase por tramo de la escalera, con `where` mutuamente excluyentes.
      labelingInfo: this.buildClusterLabelClasses(aggregates, c),
      popupEnabled: false,
      // Solo si la capa lo define: con `undefined` el SDK mantiene su default
      // (agrupamiento activo en todas las escalas).
      ...(c.maxScale !== undefined ? { maxScale: c.maxScale } : {}),
    };
  }

  /**
   * Símbolo de la burbuja de agrupamiento: círculo relleno con el color de la capa y un contorno
   * claro de contraste. El tamaño no se fija aquí —lo reparte el SDK entre `clusterMinSize` y
   * `clusterMaxSize` según el conteo—: un tamaño propio lo anularía.
   *
   * `baseColor` es el color extraído del renderer real de la capa (ver `resolveRendererColor`),
   * para que la burbuja sea reconocible como parte de la capa sin copiar su estilo exacto.
   */
  createClusterSymbol(config?: ClusterSymbolConfig, baseColor?: string): SimpleMarkerSymbol | undefined {
    const c = { ...CLUSTER_SYMBOL_DEFAULTS, ...config };
    const color = c.color ?? baseColor;
    // Sin color conocido (renderer exótico o capa aún sin renderer) se devuelve undefined: el
    // SDK conserva entonces su estilo autogenerado en lugar de dibujar una burbuja neutra.
    if (!color) return undefined;
    return new SimpleMarkerSymbol({
      style: 'circle',
      color,
      // Sin `size`: lo controla el reparto de `clusterMinSize`/`clusterMaxSize` por conteo.
      outline: new SimpleLineSymbol({ color: c.outlineColor, width: c.outlineWidth }),
    });
  }

  /**
   * Color representativo de un renderer de capa, para derivar el estilo de la burbuja del
   * cluster. Se toma el del símbolo por defecto o, en los renderers por categorías, el de la
   * primera categoría: la capa agrupa una sola serie, así que un color concreto es más legible
   * que un degradado. Devuelve `undefined` si el renderer no expone un color usable.
   */
  resolveRendererColor(renderer: unknown): string | undefined {
    const symbolColor = (symbol: any): string | undefined => this.toCssColor(symbol?.color);
    const rendererAny = renderer as any;
    if (!rendererAny) return undefined;
    // UniqueValue / ClassBreaks: el color vive en el símbolo de cada categoría.
    const categorySymbol = rendererAny.uniqueValueInfos?.[0]?.symbol ?? rendererAny.classBreakInfos?.[0]?.symbol;
    return symbolColor(categorySymbol) ?? symbolColor(rendererAny.symbol);
  }

  /** Normaliza el color de un símbolo a cadena CSS: el SDK admite `string` o `Color`. */
  private toCssColor(color: unknown): string | undefined {
    if (typeof color === 'string' && color.length > 0) return color;
    // `Color` expone `toHex()`; cualquier otro objeto se descarta.
    const asColor = color as { toHex?: () => string } | null;
    return typeof asColor?.toHex === 'function' ? asColor.toHex() : undefined;
  }

  /** Normaliza un resumen a su nombre técnico (`SUM_POB`, `MODE_ESTADO`…) + textos de etiqueta. */
  private resolveClusterAggregate(config: ClusterAggregateConfig): ClusterAggregateResolved {
    const statisticType = config.statisticType ?? 'sum';
    return {
      name: statisticType === 'count' && !config.field
        ? 'aggregateCount'
        : `${statisticType.toUpperCase()}_${config.field}`,
      alias: config.alias ?? `${statisticType.toUpperCase()} de ${config.field ?? 'los puntos agrupados'}`,
      statisticType,
      onStatisticField: config.field,
      labelPrefix: config.labelPrefix ?? '',
      unit: config.unit ?? '',
      inLabel: config.inLabel ?? true,
      numeric: config.numeric ?? statisticType !== 'mode',
    };
  }

  /**
   * Clases de etiqueta del cluster, una por tramo de la escalera de `labelSteps`.
   *
   * El SDK reparte el diámetro de la burbuja entre `clusterMinSize` (conteo 1) y
   * `clusterMaxSize` (conteo máximo) con un `SizeVariable` sobre `cluster_count`, pero
   * `Font.size` es estático —no admite expresión—, así que **la única forma soportada de que
   * la letra acompañe a la burbuja es una clase por rango de conteo** con su propio tamaño de
   * fuente. Es el mecanismo que usan los ejemplos de ArcGIS para lo mismo, y es lo que corrige
   * el defecto que se vio: una letra de 12 px fija se sale de una burbuja de 24 px y encima se
   * monta con la vecina.
   *
   * Los `where` son contiguos y excluyentes (`1..9`, `10..49`, `50..`), así que cada cluster
   * entra en exactamente una clase y el resultado no depende del orden en que las evalúe el
   * motor. El último tramo se deja abierto (`>= n`) para que ningún cluster quede sin
   * etiqueta si el conteo supera el tope declarado.
   */
  private buildClusterLabelClasses(
    aggregates: ClusterAggregateResolved[],
    config: { labelSize: number; labelSteps?: ClusterLabelStep[]; deconflictionStrategy?: 'none' | 'static' },
  ): LabelClassProperties[] {
    const steps = this.resolveClusterLabelSteps(config);
    return steps.map((step, index) => {
      const anterior = steps[index - 1];
      const esUltimo = index === steps.length - 1 || !Number.isFinite(step.maxCount);
      const limiteSuperior = esUltimo ? '' : ` AND cluster_count <= ${step.maxCount}`;
      return {
        // Rango contiguo y excluyente. `cluster_count` es el campo automático del SDK, que
        // siempre vale 1 o más, así que basta con acotar por arriba el primer tramo.
        where: anterior
          ? `cluster_count >= ${anterior.maxCount + 1}${limiteSuperior}`
          : `cluster_count <= ${step.maxCount}`,
        name: `cluster-${index + 1}`,
        // Descarte de etiquetas solapadas apagado: la etiqueta va centrada dentro de la
        // burbuja, y con `'static'` el SDK descarta la etiqueta de un cluster en cuanto su
        // texto choca con la burbuja o el texto vecino. A escala nacional, donde las burbujas
        // van muy juntas, eso vacía la vista de conteos (el propio SDK lo advierte: con
        // deconflicción activada "algunas features dejan de parecer clusters"). El solape
        // se acepta; se controla con `clusterRadius`, con `maxScale` y con el tamaño de letra.
        deconflictionStrategy: config.deconflictionStrategy ?? 'none',
        labelPlacement: 'center-center',
        labelExpressionInfo: { expression: this.buildClusterLabelExpression(aggregates, step) },
        symbol: this.buildTextSymbol({
          size: step.size,
          color: 'white',
          haloColor: 'rgba(0, 0, 0, 0.85)',
          haloSize: 1.5,
        }),
      } as LabelClassProperties;
    });
  }

  /**
   * Escalera efectiva: la de la capa si la define, y si no, un único tramo con `labelSize` y
   * todos los resúmenes —que es el comportamiento anterior a la escalera—, para que el tamaño
   * de letra siga siendo ajustable sin tocar el catálogo.
   */
  private resolveClusterLabelSteps(config: {
    labelSize: number;
    labelSteps?: ClusterLabelStep[];
    aggregates?: ClusterAggregateConfig[];
  }): ClusterLabelStep[] {
    if (config.labelSteps?.length) return config.labelSteps;
    return [{ maxCount: Number.POSITIVE_INFINITY, size: config.labelSize, includeSummaries: true }];
  }

  /**
   * ¿Hace falta agrandar `clusterMinSize`? Solo si el **primer** tramo (el de las burbujas
   * pequeñas) lleva más de una línea, porque una sola línea cabe en cualquier círculo. Un tramo
   * con `maxLines` recortado a 1 equivale a una sola línea: los renglones que se descartan no
   * ocupan sitio.
   */
  private needsBiggerMinSize(aggregates: ClusterAggregateResolved[], steps?: ClusterLabelStep[]): boolean {
    if (!aggregates.some(aggregate => aggregate.inLabel)) return false;
    const escalera = this.resolveClusterLabelSteps({ labelSize: 0, labelSteps: steps });
    return escalera[0].includeSummaries && (escalera[0].maxLines ?? 2) > 1;
  }

  /**
   * Expresión Arcade de la etiqueta del cluster: el conteo (`$feature.cluster_count`, campo
   * automático del SDK) en el primer renglón y uno por resumen marcado con `inLabel`
   * (p. ej. `536` / `POB 1,234` según el tramo).
   *
   * Comportamiento del motor comprobado ejecutando la expresión con el propio Arcade del SDK
   * (`@arcgis/core/arcade`, el mismo motor que evalúa las etiquetas en el worker 2D):
   *
   * - **Salto de línea**: `TextFormatting.NewLine` es un global que inyecta el compilador
   *   (`textformatting`, junto con `infinity`/`nan`/`pi`), así que no depende de que se
   *   registre ningún módulo de funciones. `"\n"` también funciona, pero el constante es el
   *   que documenta Esri.
   * - **Campos ausentes**: leer un campo que no está en el esquema —con punto o con corchetes—
   *   lanza `Field <campo> does not exist`. La etiqueta se evalúa como **una sola** expresión,
   *   así que ese error dejaría la burbuja **sin ninguna etiqueta**, ni siquiera el conteo:
   *   de ahí que **todas** las líneas —incluido el conteo— se protejan con
   *   `HasKey($feature, "<campo>")`, que devuelve `false` en lugar de lanzar. Con `IsEmpty()`
   *   se omite además el renglón cuando el valor es nulo (`Text(null, "#,###")` devuelve cadena
   *   vacía), así no queda el prefijo suelto ("POB "). La protección del conteo es la última
   *   defensa: si el SDK dejara de exponer `cluster_count`, la etiqueta seguiría mostrando los
   *   resúmenes en lugar de quedar en blanco.
   * - **Concatenación**: los renglones se acumulan con `Push` y se unen con
   *   `Concatenate(renglones, TextFormatting.NewLine)` en lugar de encadenar `+`.
   *
   * Los valores numéricos se formatean con separador de miles y los de texto (p. ej. el estado
   * predominante) se imprimen tal cual, porque `Text()` solo acepta números y fechas.
   */
  private buildClusterLabelExpression(aggregates: ClusterAggregateResolved[], step: ClusterLabelStep): string {
    const lines = [
      'var partes = [];',
      `if (HasKey($feature, ${this.toArcadeString('cluster_count')}) && !IsEmpty($feature.cluster_count)) {`,
      `  Push(partes, Text($feature.cluster_count, '#,###'));`,
      '}',
    ];
    // Tramo sin resúmenes: la etiqueta es solo el conteo ("9"), que es lo que cabe en una
    // burbuja pequeña. Con resúmenes, `maxLines` acota cuántos se imprimen: los que no quepan
    // no se pierden, siguen en el popup del cluster y en la leyenda.
    const cupo = step.maxLines ?? 2;
    const enLabel = step.includeSummaries ? aggregates.filter(item => item.inLabel).slice(0, cupo - 1) : [];
    for (const aggregate of enLabel) {
      const field = `$feature[${this.toArcadeString(aggregate.name)}]`;
      const value = aggregate.numeric ? `Text(${field}, '#,###')` : field;
      const prefix = aggregate.labelPrefix ? `${this.toArcadeString(`${aggregate.labelPrefix} `)} + ` : '';
      const unit = aggregate.unit ? ` + ${this.toArcadeString(` ${aggregate.unit}`)}` : '';
      lines.push(
        `if (HasKey($feature, ${this.toArcadeString(aggregate.name)})) {`,
        `  if (!IsEmpty(${field})) { Push(partes, ${prefix}${value}${unit}); }`,
        '}',
      );
    }
    lines.push('return Concatenate(partes, TextFormatting.NewLine);');
    return lines.join('\n');
  }

  /** Literal de texto para Arcade: comillas dobles con las comillas internas escapadas. */
  private toArcadeString(value: string): string {
    return `"${value.replace(/"/g, '\\"')}"`;
  }

  /**
   * Propiedades de un símbolo de texto (`TextSymbol`) para etiquetas de cliente: fuente
   * sans-serif en negrita con halo, que es lo que mantiene el texto legible sobre mapas base
   * claros y sobre la burbuja del cluster. Lo comparten la etiqueta del cluster y la etiqueta
   * de feature (`createLabelClass`) para que ambas se comporten igual ante el halo.
   */
  private buildTextSymbol(config: { size: number; color: string; haloColor: string; haloSize: number }) {
    return {
      type: 'text' as const,
      color: config.color,
      haloColor: config.haloColor,
      haloSize: config.haloSize,
      font: { family: 'sans-serif', size: config.size, weight: 'bold' as const },
    };
  }

  /**
   * Etiqueta de feature (`LabelClass`) para capas cliente que no traen etiquetas propias en
   * el servicio: p. ej. el nombre de la quebrada en las barreras dinámicas (12 puntos, que no
   * se agrupan). El texto sale de una expresión Arcade, así que no depende de la simbología
   * del servicio, y el halo lo mantiene legible sobre cualquier mapa base.
   */
  createLabelClass(config: LabelFieldConfig): LabelClassProperties {
    const c = { ...LABEL_FIELD_DEFAULTS, ...config };
    if (!c.field) {
      throw new Error('[LayerSymbolFactory] createLabelClass requiere `field`.');
    }
    return {
      labelExpressionInfo: { expression: `$feature["${c.field}"]` },
      symbol: this.buildTextSymbol({ size: c.size, color: c.color, haloColor: c.haloColor, haloSize: c.haloSize }),
      labelPlacement: c.placement,
      deconflictionStrategy: c.deconflictionStrategy,
      // 0/undefined = sin límite de escala (el SDK solo aplica el rango si se define).
      ...(c.minScale ? { minScale: c.minScale } : {}),
      ...(c.maxScale ? { maxScale: c.maxScale } : {}),
    };
  }

  /**
   * Mapa de calor de puntos (`HeatmapRenderer`, SDK >= 4.8; solo `MapView` 2D).
   *
   * Reemplaza al renderer de la capa: no dibuja símbolos individuales, de modo que en este
   * modo no hay popup por feature ni etiquetas, y es excluyente con el agrupamiento
   * (`featureReduction`) porque responde otra pregunta: "¿dónde se concentra?" y no
   * "¿qué es este punto?". Por eso se reserva a capas densas y anónimas (p. ej. ~95 000
   * centros poblados), donde dibujar un símbolo por feature es inviable.
   *
   * Requiere una capa cliente de puntos (FeatureLayer de un FeatureServer); un
   * `MapImageLayer` (MapServer) no admite renderers y se dibuja en el servidor.
   */
  createHeatmapRenderer(config?: HeatmapPointsConfig): HeatmapRenderer {
    const c = { ...HEATMAP_POINTS_DEFAULTS, ...config };
    return new HeatmapRenderer({
      radius: c.radius,
      referenceScale: c.referenceScale,
      maxDensity: c.maxDensity,
      minDensity: c.minDensity,
      // Copia defensiva: cada renderer recibe su propia rampa y no comparte la del default.
      colorStops: c.colorStops.map(stop => ({ ...stop })),
      // Sin `field`/`valueExpression` cada punto pesa 1; se omiten en vez de enviarlos
      // como `undefined` para no desactivar la ponderación que infiere el SDK.
      ...(c.field ? { field: c.field } : {}),
      ...(c.valueExpression ? { valueExpression: c.valueExpression } : {}),
    });
  }

  private hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
  }
}
